import { IListing, ISite } from '@curbnturf/entities';
import { DateTime } from 'luxon';

/** server/src/shared/constants.ts: MINIMUM_REQUIRED_PHOTOS, LISTING_INCOMPLETE_ALLOWED_DAYS. */
export const MINIMUM_PHOTOS = 3;
export const PHOTO_GRACE_DAYS = 30;

/** Something the server's publish check would refuse, labelled the way a host would say it. */
export interface PublishBlocker {
  key: string;
  label: string;
}

const blocker = (key: string, label: string): PublishBlocker => ({ key, label });

/**
 * What stops `POST listing/:id/enable`, rule for rule: listing.service.ts
 * `enable()` → `canEnable()` (+ `standardListingTests`, `testUserProfile`,
 * `missingAddress`), and site.service.ts `canEnable()` for the site the server
 * turns on with it. Photos are left to `photosBlockPublishing`.
 */
export function publishBlockers(l: IListing, now: DateTime = DateTime.now()): PublishBlocker[] {
  const out: PublishBlocker[] = [];
  // canEnable
  if (!l.coordinates?.lat || !l.coordinates?.lon) out.push(blocker('location', 'Map location'));
  if (!l.name) out.push(blocker('name', 'Name'));
  if (!l.description) out.push(blocker('description', 'Description'));
  if (!l.landTypes?.length && !l.primaryType) out.push(blocker('category', 'Property type'));
  const u = l.user;
  if (!u || !u.firstName || !u.lastName || !u.phone) out.push(blocker('profile', "Host's name and phone"));
  // standardListingTests
  if (!l.cancellationPolicy) out.push(blocker('cancellationPolicy', 'Cancellation policy'));
  if (!l.reservationProcess) out.push(blocker('reservationProcess', 'Reservation process'));
  if (!l.checkInProcess) out.push(blocker('checkInProcess', 'Check-in process'));
  if (l.bookingWindow == null) out.push(blocker('bookingWindow', 'Booking window'));
  if (l.reserveDaysInAdvance == null) out.push(blocker('reserveDaysInAdvance', 'Advance notice'));
  if (!l.minLengthOfStayInDays) out.push(blocker('minStay', 'Minimum stay'));
  if (!l.maxLengthOfStayInDays) out.push(blocker('maxStay', 'Maximum stay'));
  if (!l.checkIn) out.push(blocker('checkIn', 'Check-in time'));
  if (!l.checkOut) out.push(blocker('checkOut', 'Check-out time'));
  // canEnable: landowner, then missingAddress for a private landowner
  const lo = l.landOwner;
  const a = lo?.address;
  const missingAddress = !lo?.firstName || !lo?.lastName || !a || !a.address || !a.city || !a.state || !lo?.phone;
  if (!lo || (!lo.self && (!lo.represent || !lo.lastName || (lo.private && missingAddress)))) {
    out.push(blocker('landOwner', 'Landowner details'));
  }
  if (photosBlockPublishing(l, now))
    out.push(blocker('photos', `Photos (${l.photos?.length ?? 0} of ${MINIMUM_PHOTOS})`));
  const sites = (l.sites ?? []).filter((s) => !s.deleted);
  if (!sites.length) {
    out.push(blocker('sites', 'A site'));
    return out;
  }
  // enable(): with no site live yet, the server turns on sites[0], which is the highest id.
  if (!sites.some((s) => s.available)) {
    const first = sites.reduce((top, s) => ((s.id ?? 0) > (top.id ?? 0) ? s : top));
    out.push(...siteBlockers(first));
  }
  return out;
}

/** site.service.ts `canEnable()` + `standardSiteTests()`. */
export function siteBlockers(s: ISite): PublishBlocker[] {
  const out: PublishBlocker[] = [];
  if (!s.name) out.push(blocker('siteName', 'Site name'));
  if (!(s.amenities ?? []).some((a) => a.type === 'pullThrough' || a.type === 'backIn')) {
    out.push(blocker('parking', 'How RVs park (pull-through or back-in)'));
  }
  const v = s.vehicles;
  const anyVehicle =
    !!v &&
    (v.classA || v.classB || v.classC || v.fifthWheel || v.travelTrailer || v.teardrop || v.truckCamper || v.popup);
  if (!s.tentOnly && !anyVehicle) out.push(blocker('vehicles', 'Which RVs are allowed'));
  const p = s.privacy;
  if (!p || (!p.home && !p.neighbor && !p.others && !p.secluded)) out.push(blocker('privacy', 'Site isolation'));
  if (!s.maxGuests) out.push(blocker('maxGuests', 'Maximum guests'));
  if (!s.price && s.price !== 0) out.push(blocker('price', 'Nightly price'));
  return out;
}

/**
 * canEnable's photo rule: short of 3 photos only blocks once the 30 days after
 * publishing are within a day of running out (a null photo list is skipped).
 */
export function photosBlockPublishing(l: IListing, now: DateTime = DateTime.now()): boolean {
  if (!l.publishDate || !l.photos || l.photos.length >= MINIMUM_PHOTOS) return false;
  return DateTime.fromSeconds(l.publishDate).plus({ days: PHOTO_GRACE_DAYS }).diff(now, 'days').days <= 1;
}
